<template>
  <div
    :class="[
      'game',
      'game-path',
      (status !== undefined || showSolution) && 'game-finished',
    ]"
  >
    <Keypress
      key-event="keyup"
      @success="onKeyUp($event)"
      :prevent-default="false"
    />
    <h2>Path</h2>
    <div v-if="selectedWord" :title="selectedWord[gameTargetLang]">
      <div :class="'word-' + gameSrcLang">{{ selectedWord[gameSrcLang] }}</div>
      <!-- div class="word-path-json">{{ JSON.stringify(path) }}</div -->
      <div v-if="path.length > 0" class="word-path">
        <div v-for="(row, idxRow) in path" :key="'row-' + idxRow" class="row">
          <div
            v-for="(cell, idxCol) in row"
            :class="[
              'cell',
              cell.ok && 'cell-ok',
              showSolution && cell.n >= 0 && 'cell-solution cell-ok',
              cell.n !== selectedWord[gameTargetLang].length - 1 &&
                cell.n === found &&
                'cell-current',
              cell.n === selectedWord[gameTargetLang].length - 1 &&
                cell.n === found &&
                'cell-last',
            ]"
            :data-prev-cell="cell.prev"
            :data-n="cell.n >= 0 && '' + cell.n"
            :key="'cell-' + idxRow + '-' + idxCol"
            :title="'' + cell.n + (cell.prev ? '-' + cell.prev : '')"
            @click="onClickCell(cell)"
          >
            {{ cell ? cell.c : " " }}
          </div>
        </div>
      </div>
      <div v-if="status" :class="'word-' + gameTargetLang">
        <icelandic-term :term="selectedWord[gameTargetLang]" />
      </div>
    </div>
  </div>
</template>

<script>
import IcelandicTerm from "@/components/IcelandicTerm.vue";
import { mapGetters } from "vuex";
const _ = require("lodash");
const helpers = require("@/services/helpers.js");
const constants = require("@/constants.js");

function cloneMatrix(m) {
  let clone = _.cloneDeep(m);
  return clone;
}

function buildMatrix(w, h) {
  let m = [];
  for (let ii = 0; ii < w; ii++) {
    let col = [];
    for (let jj = 0; jj < h; jj++) {
      col.push(undefined);
    }
    m.push(col);
  }
  return m;
}

function finalizeMatrix(cols) {
  if (!cols) {
    console.log("Found no path!");
    return [];
  }

  let rows = [];
  let firstCell = null;

  for (let yy = 0; yy < cols[0].length; yy++) {
    let r = "";
    let row = [];
    for (let xx = 0; xx < cols.length; xx++) {
      let col = cols[xx];

      // for console.log
      let info;
      if (col[yy]) {
        info = ("" + col[yy].n).padEnd(2, " ");
        info += `(${info})`;
      } else {
        info = "    ";
      }
      // info = "";
      if (col[yy]) {
        r += ` ${col[yy].c}${info} `;
      } else {
        r += ` .${info} `;
      }
      //

      // fill empty cell with dummy letter
      if (!col[yy]) {
        let lettersAround = [];
        if (yy > 0 && cols[xx][yy - 1]) {
          lettersAround.push(cols[xx][yy - 1].c);
        }
        if (yy + 1 < cols[xx].length && cols[xx][yy + 1]) {
          lettersAround.push(cols[xx][yy + 1].c);
        }
        if (xx > 0 && cols[xx - 1][yy]) {
          lettersAround.push(cols[xx - 1][yy].c);
        }
        if (xx + 1 < cols.length && cols[xx + 1][yy]) {
          lettersAround.push(cols[xx + 1][yy].c);
        }
        let randomLetters = constants.ALPHABET.filter(
          (c) => !lettersAround.includes(c)
        );
        let randomLetter =
          randomLetters[helpers.randomInteger(0, randomLetters.length - 1)];
        col[yy] = { n: -1, c: randomLetter, x: xx, y: yy };
      }

      if (col[yy].n === 0) {
        col[yy].ok = true;
        firstCell = col[yy];
      }

      row.push(col[yy]);
    }

    console.log(r);

    rows.push(row);
  }

  return { path: rows, firstCell };
}

function findPath(str, depth, currx, curry, maxw, maxh, currmatrix) {
  if (str.length == 0) return currmatrix;

  let possibleMoves = [];
  if (curry - 1 >= 0 && !currmatrix[currx][curry - 1]) {
    possibleMoves.push({ dx: 0, dy: -1, prev: "S" });
  }
  if (curry + 1 < maxh && !currmatrix[currx][curry + 1]) {
    possibleMoves.push({ dx: 0, dy: 1, prev: "N" });
  }
  if (currx - 1 >= 0 && !currmatrix[currx - 1][curry]) {
    possibleMoves.push({ dx: -1, dy: 0, prev: "E" });
  }
  if (currx + 1 < maxw && !currmatrix[currx + 1][curry]) {
    possibleMoves.push({ dx: 1, dy: 0, prev: "W" });
  }
  if (possibleMoves.length == 0) {
    return null;
  }
  if (possibleMoves.length > 1) {
    helpers.shuffleArray(possibleMoves);
  }

  for (let move of possibleMoves) {
    let matrix = cloneMatrix(currmatrix);
    let x = currx + move.dx;
    let y = curry + move.dy;
    matrix[x][y] = { n: depth, x, y, c: str[0], prev: move.prev };
    let path = findPath(str.substring(1), depth + 1, x, y, maxw, maxh, matrix);
    if (path) return path;
  }
  return null;
}

function buildPath(word) {
  let wordLen = word.length;
  if (wordLen == 0) {
    return [];
  }

  let matrixSize;
  if (wordLen >= 18) {
    matrixSize = 6;
  } else if (wordLen >= 13) {
    matrixSize = 5;
  } else if (wordLen >= 7) {
    matrixSize = 4;
  } else {
    matrixSize = 3;
  }

  let matrix = buildMatrix(matrixSize, matrixSize);
  let x = helpers.randomInteger(0, matrixSize - 1);
  let y = helpers.randomInteger(0, matrixSize - 1);
  matrix[x][y] = { n: 0, x, y, c: word[0] };

  let path = findPath(
    word.substring(1),
    1,
    x,
    y,
    matrixSize,
    matrixSize,
    matrix
  );
  return finalizeMatrix(path);
}

export default {
  name: "GamePath",
  components: {
    IcelandicTerm,
    Keypress: () => import("vue-keypress"),
  },
  data() {
    const emptyWord = {};
    emptyWord[this.gameSrcLang] = "";
    emptyWord[this.gameTargetLang] = "";
    return {
      path: [],
      currentCell: null,
      found: 0,
      status: undefined,
      emptyWord,
      showSolution: false,
      errors: 0,
    };
  },
  mounted() {
    console.log("GamePath mounted: " + JSON.stringify(this.selectedWord));
    this.initGame();
  },
  updated() {
    // if (this.selectedWord) {
    //   console.log("GamePath updated: " + JSON.stringify(this.selectedWord));
    //   this.path = buildPath(this.selectedWord[this.gameTargetLang]);
    // }
  },
  computed: {
    ...mapGetters([
      "gameSrcLang",
      "gameTargetLang",
      "gameAllowedErrors",
      "selectedWords",
    ]),
    selectedWord() {
      if (this.selectedWords.length == 0) return this.emptyWord;
      for (let word of this.selectedWords) {
        if (word.failed) return word;
      }
      return this.selectedWords[0];
    },
  },
  created() {
    // console.log(
    //   `created ${this.gameSrcLang.toUpperCase()} to ${this.gameTargetLang.toUpperCase()}`
    // );
    this.$store.subscribe((mutation, state) => {
      if (["CATEGORY_SELECTED", "NEW_GAME"].includes(mutation.type)) {
        this.initGame();
      }
    });
  },
  methods: {
    onClickCell(cell) {
      if (this.showSolution) return;

      if (cell.ok) return; // already clicked and part of the correct path
      if (this.found + 1 === this.selectedWord[this.gameTargetLang].length) {
        return;
      }

      let ok;
      if (cell.n === this.found) {
        ok = true;
      } else if (cell.n === this.found + 1) {
        cell.ok = true;
        this.currentCell = cell;
        this.found++;
        ok = true;
      } else {
        ok = false;
      }

      if (ok) {
        if (this.found + 1 === this.selectedWord[this.gameTargetLang].length) {
          this.setGameStatus(true);
        } else {
          this.setGameStatus(undefined);
        }
      } else {
        this.setGameStatus(false);
      }
    },
    onKeyUp(e) {
      if (this.showSolution) return;

      if (e.event.altKey) return;
      if (this.path.length == 0) return;
      // up:38 down:40 left:37 right:39
      if (![37, 38, 39, 40].includes(e.event.keyCode)) return;

      let currX = this.currentCell.x;
      let currY = this.currentCell.y;
      let maxX = this.path[0].length - 1;
      let maxY = this.path.length - 1;
      switch (e.event.keyCode) {
        case 38:
          currY--;
          break;
        case 40:
          currY++;
          break;
        case 37:
          currX--;
          break;
        case 39:
          currX++;
          break;
      }
      if (currX >= 0 && currX <= maxX && currY >= 0 && currY <= maxY) {
        let newCell = this.path[currY][currX];
        this.onClickCell(newCell);
      }
    },
    initGame() {
       console.log(
        `GamePath initGame`
      );

      this.found = 0;
      let pathInfo = buildPath(this.selectedWord[this.gameTargetLang]);
      this.showSolution = false;
      this.path = pathInfo.path;
      this.currentCell = pathInfo.firstCell;
      this.setGameStatus(undefined);
      this.errors = 0;
    },
    setGameStatus(status) {
      if (status === false) {
        this.errors++;
        if (this.errors < this.gameAllowedErrors) return;
      }
      this.status = status;
      this.$emit("gameStatus", status);
    },
    solution() {
      this.showSolution = true;
    },
  },
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
@import "@/scss/components-shared.scss";

.word-path {
  display: flex;
  flex-direction: column;
}
.row {
  display: flex;
  flex: 0;
  height: 40px;
  margin-top: 12px;
  margin-bottom: 12px;
  justify-content: center;
}
.cell {
  display: flex;
  flex: 0 0 40px;
  height: 40px;
  margin-left: 12px;
  margin-right: 12px;
  justify-content: center;
  align-items: center;
  border-radius: 20px;
  border: 1px solid #2c3161;
  cursor: pointer;
  background-color: #949fdc;
}
.cell-ok {
  border-width: 3px;
  position: relative;

  &.cell-last {
    border-color: green;
  }
}
.cell-ok.cell-solution {
  border-color: orange;
}
.cell-ok[data-prev-cell="E"]::before {
  height: 10px;
  width: 26px;
  content: " ";
  position: absolute;
  background-color: #2c3161;
  left: 36px;
}
.cell-ok[data-prev-cell="E"].cell-solution::before {
  background-color: orange;
}
.cell-ok[data-prev-cell="W"]::before {
  height: 10px;
  width: 26px;
  content: " ";
  position: absolute;
  background-color: #2c3161;
  left: -28px;
}
.cell-ok[data-prev-cell="W"].cell-solution::before {
  background-color: orange;
}
.cell-ok[data-prev-cell="S"]::before {
  width: 10px;
  height: 26px;
  content: " ";
  position: absolute;
  background-color: #2c3161;
  top: 36px;
}
.cell-ok[data-prev-cell="S"].cell-solution::before {
  background-color: orange;
}
.cell-ok[data-prev-cell="N"]::before {
  width: 10px;
  height: 26px;
  content: " ";
  position: absolute;
  background-color: #2c3161;
  top: -28px;
}
.cell-ok[data-prev-cell="N"].cell-solution::before {
  background-color: orange;
}

@keyframes last {
  0% {
    background-color: white;
  }
  100% {
    background-color: #ccc;
  }
}
.cell-current {
  animation: last 1s linear infinite alternate;
}

.word-is {
  margin-top: 1rem;
}
</style>
